import React from "react";
import { Alert, Button, List, Modal, Progress, Space, Tag, Typography } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined, WarningOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

const { Title, Text } = Typography;

dayjs.extend(utc);
dayjs.extend(timezone);

const ProcessOrdersProgressModal = ({
  visible,
  onClose,
  progress,
  currentOrder,
  currentOperation,
  completedOrders,
  isComplete,
  summary,
}) => {
  const { t } = useTranslation();

  // Detect stock failures across completed orders
  const stockFailures = Array.isArray(completedOrders)
    ? completedOrders.filter(
        (item) => item?.operations?.stock && item.operations.stock.attempted && !item.operations.stock.success,
      )
    : [];
  const hasStockFailures = stockFailures.length > 0;

  const downloadStockFailureLog = () => {
    try {
      const payload = stockFailures.map((item) => ({
        farmorderId: item.farmorderId,
        customer: item.order?.farmorderCustomerName,
        deliveryDate: item.order?.farmorderDeliveryDate,
        status: item.status,
        stock: {
          error: item.operations?.stock?.error || null,
          response: item.operations?.stock?.response || null,
        },
        processOrder: {
          success: item.operations?.processOrder?.success,
          error: item.operations?.processOrder?.error || null,
          response: item.operations?.processOrder?.response || null,
        },
      }));

      const blob = new Blob(
        [
          JSON.stringify(
            {
              generatedAt: dayjs().tz("Europe/Amsterdam").format("YYYY-MM-DDTHH:mm:ssZ"),
              count: payload.length,
              failures: payload,
            },
            null,
            2,
          ),
        ],
        { type: "application/json" },
      );

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `stock-failures-${dayjs().format("YYYYMMDD-HHmmss")}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Failed to generate stock failure log", e);
    }
  };

  // Detect processOrder failures
  const processFailures = Array.isArray(completedOrders)
    ? completedOrders.filter(
        (item) =>
          item?.operations?.processOrder &&
          item.operations.processOrder.attempted &&
          !item.operations.processOrder.success,
      )
    : [];
  const hasProcessFailures = processFailures.length > 0;

  const downloadProcessFailureLog = () => {
    try {
      const payload = processFailures.map((item) => ({
        farmorderId: item.farmorderId,
        customer: item.order?.farmorderCustomerName,
        deliveryDate: item.order?.farmorderDeliveryDate,
        status: item.status,
        processOrder: {
          error: item.operations?.processOrder?.error || null,
          response: item.operations?.processOrder?.response || null,
        },
        stock: {
          success: item.operations?.stock?.success,
          error: item.operations?.stock?.error || null,
          response: item.operations?.stock?.response || null,
        },
      }));

      const blob = new Blob(
        [
          JSON.stringify(
            {
              generatedAt: dayjs().tz("Europe/Amsterdam").format("YYYY-MM-DDTHH:mm:ssZ"),
              count: payload.length,
              failures: payload,
            },
            null,
            2,
          ),
        ],
        { type: "application/json" },
      );

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `process-failures-${dayjs().format("YYYYMMDD-HHmmss")}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Failed to generate processOrder failure log", e);
    }
  };

  const getOperationDisplayName = (operation) => {
    const names = {
      firestore: t("creating_order"),
      processOrder: t("processing_order"),
      stock: t("updating_stock"),
    };
    return names[operation] || operation;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "success":
        return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
      case "error":
        return <CloseCircleOutlined style={{ color: "#ff4d4f" }} />;
      case "partial":
        return <WarningOutlined style={{ color: "#faad14" }} />;
      case "processing":
        return <LoadingOutlined style={{ color: "#1890ff" }} />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "success":
        return "success";
      case "error":
        return "error";
      case "partial":
        return "warning";
      case "processing":
        return "processing";
      default:
        return "default";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "success":
        return t("success");
      case "error":
        return t("failed");
      case "partial":
        return t("partial");
      case "processing":
        return t("processing");
      default:
        return status;
    }
  };

  const renderOperationDetails = (operations) => {
    if (!operations) return null;

    return (
      <div style={{ marginTop: 8, fontSize: "12px" }}>
        {Object.entries(operations).map(([operation, result]) => {
          if (!result.attempted) return null;

          return (
            <div key={operation} style={{ marginBottom: 8 }}>
              {/* Header row */}
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {result.success ? (
                  <CheckCircleOutlined style={{ color: "#52c41a", fontSize: "12px" }} />
                ) : (
                  <CloseCircleOutlined style={{ color: "#ff4d4f", fontSize: "12px" }} />
                )}
                <Text type={result.success ? "secondary" : "danger"} style={{ fontSize: "12px" }}>
                  {getOperationDisplayName(operation)}: {result.success ? t("success") : t("failed")}
                </Text>
              </div>

              {/* Error line */}
              {result.error && (
                <div style={{ marginLeft: 24, marginTop: 2 }}>
                  <Text type="danger" style={{ fontSize: "11px" }}>
                    ({result.error})
                  </Text>
                </div>
              )}

              {/* Primary API message if available */}
              {!result.success && result.response?.message && (
                <div style={{ marginLeft: 24, marginTop: 2 }}>
                  <Text type="danger" style={{ fontSize: "11px" }}>
                    {result.response.message}
                  </Text>
                </div>
              )}

              {/* For processOrder, surface sub-operation errors in response.operations */}
              {!result.success && operation === "processOrder" && result.response?.operations && (
                <div style={{ display: "block", marginLeft: 24, marginTop: 4 }}>
                  {Object.entries(result.response.operations).map(([subOp, subRes]) => {
                    if (!subRes?.attempted) return null;
                    const subOk = !!subRes?.success;
                    return (
                      <div key={subOp} style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
                        {subOk ? (
                          <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 10 }} />
                        ) : (
                          <CloseCircleOutlined style={{ color: "#ff4d4f", fontSize: 10 }} />
                        )}
                        <Text type={subOk ? "secondary" : "danger"} style={{ fontSize: 11 }}>
                          {subOp}: {subOk ? t("success") : t("failed")}
                        </Text>
                        {!subOk && subRes?.error && (
                          <Text type="danger" style={{ fontSize: 11 }}>
                            ({subRes.error})
                          </Text>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Modal
      title={
        <Space>
          <Title level={4} style={{ margin: 0 }}>
            {isComplete ? t("order_processing_complete") : t("processing_orders")}
          </Title>
          <Tag color="processing">
            {progress.current} / {progress.total}
          </Tag>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={
        isComplete
          ? [
              <Button key="close" type="primary" onClick={onClose}>
                {t("close")}
              </Button>,
            ]
          : null
      }
      width={800}
      style={{ top: 20 }}
      closable={isComplete}
      maskClosable={false}
    >
      {/* Progress Bar */}
      <div style={{ marginBottom: 24 }}>
        <Progress
          percent={Math.round((progress.current / progress.total) * 100)}
          status={isComplete ? "success" : "active"}
          showInfo={true}
        />
      </div>

      {/* Current Order Being Processed */}
      {!isComplete && currentOrder && (
        <div
          style={{
            marginBottom: 24,
            padding: 16,
            backgroundColor: "#f5f5f5",
            borderRadius: 6,
            border: "1px solid #d9d9d9",
          }}
        >
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <Text strong>{t("currently_processing")}:</Text>
            <Text style={{ fontSize: "16px" }}>{currentOrder.farmorderCustomerName}</Text>
            <Text type="secondary">
              {t("delivery_date")}: {dayjs(currentOrder.farmorderDeliveryDate).format("dddd DD-MM-YYYY")}
            </Text>
            {currentOperation && (
              <Text type="secondary" style={{ fontStyle: "italic", color: "#1890ff" }}>
                {getOperationDisplayName(currentOperation)}...
              </Text>
            )}
          </Space>
        </div>
      )}

      {/* Summary when complete */}
      {isComplete && summary && (
        <div style={{ marginBottom: 24 }}>
          {summary.successCount > 0 && (
            <Alert
              message={`${summary.successCount} ${t("orders_processed_successfully")}`}
              type="success"
              showIcon
              style={{ marginBottom: 8 }}
            />
          )}
          {summary.partialCount > 0 && (
            <Alert
              message={`${summary.partialCount} ${t("orders_processed_with_warnings")}`}
              type="warning"
              showIcon
              style={{ marginBottom: 8 }}
            />
          )}
          {summary.errorCount > 0 && (
            <Alert
              message={`${summary.errorCount} ${t("orders_failed_to_process")}`}
              type="error"
              showIcon
              style={{ marginBottom: 8 }}
            />
          )}

          {/* Prominent Stock Failure Alert with log download */}
          {hasStockFailures && (
            <Alert
              message={t("stock_update_failed")}
              description={t("some_orders_stock_failed")}
              type="error"
              showIcon
              style={{ marginTop: 16 }}
              action={
                <Button size="small" type="primary" onClick={downloadStockFailureLog}>
                  {t("download_error_log")}
                </Button>
              }
            />
          )}

          {/* Prominent ProcessOrder Failure Alert with log download */}
          {hasProcessFailures && (
            <Alert
              message={t("process_order_failed")}
              description={t("some_orders_process_failed")}
              type="error"
              showIcon
              style={{ marginTop: 16 }}
              action={
                <Button size="small" type="primary" onClick={downloadProcessFailureLog}>
                  {t("download_error_log")}
                </Button>
              }
            />
          )}
        </div>
      )}

      {/* Order Status List */}
      <div>
        <Title level={5} style={{ marginBottom: 16 }}>
          {t("order_status")}
        </Title>

        <List
          dataSource={completedOrders}
          renderItem={(item, index) => (
            <List.Item
              style={{
                padding: "12px 0",
                borderBottom: index < completedOrders.length - 1 ? "1px solid #f0f0f0" : "none",
              }}
            >
              <List.Item.Meta
                avatar={getStatusIcon(item.status)}
                title={
                  <Space>
                    <Text strong>#{index + 1}</Text>
                    <Text>{item.order.farmorderCustomerName}</Text>
                    <Tag color={getStatusColor(item.status)} size="small">
                      {getStatusText(item.status)}
                    </Tag>
                    {/* Stock failure badge so the user cannot miss it */}
                    {item?.operations?.stock?.attempted && !item?.operations?.stock?.success && (
                      <Tag color="error" size="small">
                        {t("stock_failed")}
                      </Tag>
                    )}
                    {item.farmorderId && (
                      <Text type="secondary" style={{ fontSize: "12px" }}>
                        ID: {item.farmorderId}
                      </Text>
                    )}
                  </Space>
                }
                description={
                  <div>
                    <Text type="secondary" style={{ fontSize: "13px" }}>
                      {dayjs(item.order.farmorderDeliveryDate).format("ddd DD-MM-YYYY")}
                    </Text>

                    {/* Show operation details if available */}
                    {item.operations && renderOperationDetails(item.operations)}

                    {/* Show general error if no operations details */}
                    {item.error && !item.operations && (
                      <div style={{ marginTop: 4 }}>
                        <Text type="danger" style={{ fontSize: "12px" }}>
                          {t("error")}: {item.error}
                        </Text>
                      </div>
                    )}
                  </div>
                }
              />
            </List.Item>
          )}
          style={{
            maxHeight: 350,
            overflowY: "auto",
            border: "1px solid #f0f0f0",
            borderRadius: 6,
            padding: "8px 16px",
            backgroundColor: "#fafafa",
          }}
          locale={{
            emptyText: t("no_orders_processed"),
          }}
        />
      </div>
    </Modal>
  );
};

export default ProcessOrdersProgressModal;
